'use client'

/**
 * RecommendationsGrid - Unified recommendations display for all tools
 * Replaces inconsistent tip sections with standardized 3-column grid
 */

import React, { useState } from 'react'
import { Card } from '@/components/ui/card'
import {
  CheckCircle2,
  Lightbulb,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  LucideIcon
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { RecommendationSection } from './types'
import { recommendationColors } from '@/lib/design-tokens'

interface RecommendationsGridProps {
  sections: RecommendationSection[]
  columns?: 1 | 2 | 3
  className?: string
}

const defaultIcons: Record<string, LucideIcon> = {
  success: CheckCircle2,
  info: Lightbulb,
  warning: AlertTriangle,
  danger: XCircle,
}

export function RecommendationsGrid({
  sections,
  columns = 3,
  className,
}: RecommendationsGridProps) {
  const gridColsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  }[columns]

  return (
    <div className={cn('grid gap-6', gridColsClass, className)}>
      {sections.map((section, index) => (
        <RecommendationCard key={index} section={section} />
      ))}
    </div>
  )
}

/**
 * Individual recommendation section card
 */
function RecommendationCard({ section }: { section: RecommendationSection }) {
  const [isExpanded, setIsExpanded] = useState(section.defaultExpanded ?? true)

  const Icon = section.icon || defaultIcons[section.variant]
  const colors = recommendationColors[section.variant]

  const handleToggle = () => {
    if (section.collapsible) {
      setIsExpanded(!isExpanded)
    }
  }

  return (
    <Card className={cn(
      'overflow-hidden border',
      colors.border
    )}>
      {/* Section Header */}
      <div
        className={cn(
          'flex items-center justify-between p-4',
          colors.background,
          section.collapsible && 'cursor-pointer hover:opacity-80 transition-opacity'
        )}
        onClick={handleToggle}
        role={section.collapsible ? 'button' : undefined}
        tabIndex={section.collapsible ? 0 : undefined}
        onKeyDown={section.collapsible ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
          }
        } : undefined}
        aria-expanded={section.collapsible ? isExpanded : undefined}
      >
        <div className="flex items-center gap-3">
          {Icon && (
            <div className={cn(
              'p-2 rounded-lg',
              section.variant === 'success' && 'bg-green-100',
              section.variant === 'info' && 'bg-gray-100',
              section.variant === 'warning' && 'bg-amber-100',
              section.variant === 'danger' && 'bg-red-100'
            )}>
              <Icon className={cn('h-5 w-5', colors.icon)} aria-hidden="true" />
            </div>
          )}
          <h3 className={cn('font-semibold', colors.text)}>
            {section.title}
          </h3>
        </div>

        {section.collapsible && (
          <div className={colors.icon}>
            {isExpanded ? (
              <ChevronUp className="h-5 w-5" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-5 w-5" aria-hidden="true" />
            )}
          </div>
        )}
      </div>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-4 space-y-3">
          {section.items.length === 0 ? (
            <p className="text-sm text-gray-600 italic">
              No recommendations available
            </p>
          ) : (
            <ul className="space-y-2" role="list">
              {section.items.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className={cn(
                    'mt-0.5 flex-shrink-0',
                    colors.icon
                  )}>
                    <div className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {item}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  )
}

/**
 * Preset section configurations for common use cases
 */
export const presetSections = {
  immediateActions: (items: string[]): RecommendationSection => ({
    title: 'Immediate Actions',
    items,
    variant: 'success',
    icon: CheckCircle2,
  }),

  bestPractices: (items: string[]): RecommendationSection => ({
    title: 'Best Practices',
    items,
    variant: 'info',
    icon: Lightbulb,
  }),

  thingsToAvoid: (items: string[]): RecommendationSection => ({
    title: 'Things to Avoid',
    items,
    variant: 'danger',
    icon: XCircle,
  }),

  warnings: (items: string[]): RecommendationSection => ({
    title: 'Important Warnings',
    items,
    variant: 'warning',
    icon: AlertTriangle,
  }),
}
